import React, { useState, useRef, useEffect } from 'react';
import { useIntl } from 'react-intl';
import validator from 'validator';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles } from '@material-ui/core/styles';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import FormControl from '@material-ui/core/FormControl';
import Visibility from '@material-ui/icons/Visibility';
import { FormattedMessage } from 'react-intl';
import FirebaseEventManager from '../../utils/firebaseEventManager.js';

import SignInBtn from './SignInBtn';
import logoImg from '../images/darkLogo.png';

import apImg from '../images/ap.png';
import gpImg from '../images/gp.png';

function EmailSignIn(props) {
  const {
    signInWithGoogle,
    signInWithEmail,
    signInWithApple,
    fetchSignInMethodsForEmail,
  } = props;
  const firebaseEventManager = new FirebaseEventManager();
  const intl = useIntl();

  const [isShowPassword, setIsShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    firebaseEventManager.signInEmailPageLanded();
  }, []);

  const emailInputEl = useRef(null);

  const { classes } = props;

  async function changeEmail(event) {
    setEmail(event.target.value);
    if (!checkEmailFormat()) {
      setEmailError('email_signin_page_email_invalid');
      return false
    } else {
      setEmailError('');
      return true
    }
  }
  function changePassword(event) {
    setPassword(event.target.value);
    setPasswordError('');
  }

  async function checkUserEmail() {
    if (!checkEmailFormat()) {
      setEmailError('email_signin_page_email_invalid');
      return false
    }
    const result = await fetchSignInMethodsForEmail(email);
    if (result.length === 0) {
      setEmailError('email_signin_page_email_unregistered');
      return false 
    }
    setEmailError('');
    return true
  }

  function checkEmailFormat() {
    return validator.isEmail(email);
  }

  async function submit() {
    if (isSubmitting) {
      return;
    } 
    if(emailError !== ''){
      return;
    }
    if (email === '' || password === '') {
      return;
    }
    try {
      setIsSubmitting(true);

      firebaseEventManager.signInEmailPageClick({
        item: 'email',
      });

      const emailValidateResult = await checkUserEmail();
      if (!emailValidateResult) {
        return;
      }

      const canTry = canTryPassword(email);
      if (!canTry) {
        return await signInWithEmail({
          error: 'ExceedTryMax',
        });
      }
      const result = await signInWithEmail({
        email,
        password,
      });
      if (result === true) {
        resetTryTime(email);
      }
    } catch (error) {
      const isGmailRegex = /(gmail\.com$)|(googlemail\.com$)/i;
      if (
        isGmailRegex.test(email) &&
        (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found')
      ) {
        tryPasswordFailed(email);
        firebaseEventManager.sendLoginFailed({
          provider: 'email',
          type: 'manual',
          code: error.code,
          customError: 'please_try_google',
        });
        return setPasswordError('email_signin_page_use_google_error');
      }

      if (error.code === 'auth/wrong-password') {
        tryPasswordFailed(email);
        firebaseEventManager.sendLoginFailed({
          provider: 'email',
          type: 'manual',
          code: error.code,
          customError: 'wrong_password',
        });
        return setPasswordError('email_signin_page_wrong_email_pwd');
      }

      if (error.code === 'auth/user-not-found') {
        // to distinguish apple private sign in 
        const signInMethods = await fetchSignInMethodsForEmail(email);
        if (signInMethods.length > 0){
          tryPasswordFailed(email);
          firebaseEventManager.sendLoginFailed({
            provider: 'email',
            type: 'manual',
            code: error.code,
            customError: 'wrong_password',
          });
          return setPasswordError('email_signin_page_wrong_email_pwd');
        }
        return setEmailError('email_signin_page_email_unregistered');
      }

      return signInWithEmail({
        error,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const isSubmitBtnValid = email.length > 0 && password.length > 0;
  const email_signin_page_signup_tip = intl.formatMessage({
    id: 'email_signin_page_signup_tip',
  });
  const sign_in_same_account = intl.formatMessage({
    id: 'sign_in_same_account',
  });
  const hasError = emailError !== '' || passwordError !== '';
  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <p className={classes.imageContainer}>
          <img src={logoImg} width="240"></img>
        </p>
        <p className={classes.signInIntro} dangerouslySetInnerHTML={{ __html: sign_in_same_account }}></p>
        <div className={classes.form}>
          <FormControl className={classes.formColumn}>
            <p className={classes.formLabel}>
              <FormattedMessage id="email_signin_page_email"></FormattedMessage>
            </p>
            <Input
              type="text"
              value={email}
              inputRef={emailInputEl}
              onChange={changeEmail}
              onBlur={checkUserEmail}
              className={emailError !== '' ? 'Mui-error' : ''}
            />
            <p className={emailError !== '' ? classes.formErrorText_visible : classes.formErrorText}>
              {emailError !== '' && <FormattedMessage id={emailError}></FormattedMessage>}
            </p>
          </FormControl>
          <FormControl className={classes.formColumn}>
            <p className={classes.formLabel}>
              <FormattedMessage id="email_signin_page_password"></FormattedMessage>
            </p>
            <Input
              id="standard-adornment-password"
              type={isShowPassword ? 'text' : 'password'}
              className={passwordError !== '' ? `${classes.formInput} Mui-error` : classes.formInput}
              value={password}
              onChange={changePassword}
              endAdornment={
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setIsShowPassword(!isShowPassword)}
                  >
                    {isShowPassword ? <Visibility /> : <Visibility className={classes.hidePassword} />}
                  </IconButton>
                </InputAdornment>
              }
            />
            <p className={passwordError !== '' ? classes.formErrorText_visible : classes.formErrorText}>
              {passwordError !== '' && <FormattedMessage id={passwordError}></FormattedMessage>}
            </p>
          </FormControl>
          <button
            // disabled={!isSubmitBtnValid && !isSubmitting}
            className={isSubmitBtnValid && !hasError ? classes.emailSignInBtn : classes.emailSignInBtnDisabled}
            onClick={submit}
            style={{ color: '#FFFFFF' }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" id="signInLoading" />
            ) : (
              <FormattedMessage id="email_signin_page_email_signin_btn" />
            )}
          </button>
          <p className={classes.separateLine}></p>
          <div className={classes.signInBtn} onClick={(event) => signInWithGoogle()}>
            <SignInBtn type="google" />
          </div>
          <div className={classes.signInBtn} onClick={(event) => signInWithApple()}>
            <SignInBtn type="apple" />
          </div>
        </div>

        <p
          className={classes.signInFooter}
          dangerouslySetInnerHTML={{ __html: email_signin_page_signup_tip }}
        ></p>
        <p className={classes.imageContainer}>
          <img
            onClick={() => props.downloadApp({ platform: 'ios', page: 'emailsignin' })}
            className={classes.apImg}
            src={apImg}
            width="136"
          />
          <img
            onClick={() => props.downloadApp({ platform: 'android', page: 'emailsignin' })}
            className={classes.gpImg}
            src={gpImg}
            width="134"
          />
        </p>
      </div>
    </div>
  );
}

const storageKey = 'Alfred::trypasswrod';
function resetTryTime(email) {
  let data = localStorage.getItem(storageKey);
  if (data) {
    data = JSON.parse(data);
    data[email] = [];
    localStorage.setItem(storageKey, JSON.stringify(data));
  }
}
function canTryPassword(email) {
  let data = localStorage.getItem(storageKey);
  const now = new Date().getTime();
  try {
    if (data) {
      data = JSON.parse(data);
      if (data[email]) {
        // this should be timestamp list
        const twentyFour = 24 * 60 * 60 * 1000;
        const in24HourLockList = data[email].filter((t) => now - t < twentyFour);
        if (in24HourLockList.length >= 10) {
          return false;
        }
      }
    } 
    return true;
  }catch(error) {};
}
function tryPasswordFailed(email) {
  let data = localStorage.getItem(storageKey);
  const now = new Date().getTime();

  try {
    if (data) {
      data = JSON.parse(data);
      if (data[email]) {
        // this should be timestamp list
        const twentyFour = 24 * 60 * 60 * 1000;
        const in24HourLockList = data[email].filter((t) => now - t < twentyFour);
        if (in24HourLockList.length >= 10) {
          return false;
        } else {
          in24HourLockList.push(now);
          data[email] = in24HourLockList;
          return true;
        }
      } else {
        data[email] = [now];
        return true;
      }
    } else {
      data = {
        [email]: [now],
      };
      return true;
    }
  } finally {
    localStorage.setItem(storageKey, JSON.stringify(data));
  }
}


const styles = (theme) => ({
  root: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    overflow: 'auto',
    background: '#F8F8F8',
    padding: `32px 0`,
    fontFamily: 'Open Sans',
  },
  container: {
    margin: 'auto',
  },
  imageContainer: {
    textAlign: 'center',
  },
  signInIntro: {
    fontFamily: 'Open Sans',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '17px',
    lineHeight: '23px',
    textAlign: 'center',
    color: '#757575',
    margin: '16px auto',
    maxWidth: 375,
  },
  form: {
    padding: 32,
    paddingBottom: 16,
    background: '#FFFFFF',
    border: '1px solid #E1E1E1',
    boxSizing: 'border-box',
    borderRadius: 4,
    maxWidth: 375,
    margin: 'auto',
  },
  formLabel: {
    fontFamily: 'Open Sans',
    fontStyle: 'normal',
    fontWeight: 600,
    fontSize: '15px',
    lineHeight: '20px',
    color: '#757575',
    textAlign: 'left',
    marginBottom: 15,
  },
  formInput: {
    height: 32,
  },
  formColumn: {
    width: 310,
    marginBottom: 32,
    position: 'relative',
    '&:first-child': {
      marginBottom: 48,
    },
  },
  formErrorText: {
    fontSize: 12,
    lineHeight: '16px',
    color: '#FF7600',
    textAlign: 'left',
    visibility: 'hidden',
    height: 20,
    marginTop: 3,
  },
  formErrorText_visible: {
    fontSize: 12,
    lineHeight: '16px',
    color: '#FF7600',
    textAlign: 'left',
    visibility: 'visible',
    height: 20,
    marginTop: 3,
  },
  hidePassword: {
    '& > *': {
      fill: '#E1E1E1',
    },
  },
  emailSignInBtnDisabled: {
    width: '100%',
    height: 48,
    background: '#E1E1E1',
    borderRadius: 4,
    fontWeight: 600,
    fontSize: 17,
    lineHeight: '23px',
    textAlign: 'center',
    color: '#FFFFFF',
    border: 'initial',
    cursor: '',
  },
  emailSignInBtn: {
    width: '100%',
    height: 48,
    background: '#FD9728',
    borderRadius: 4,
    fontWeight: 600,
    fontSize: 17,
    lineHeight: '23px',
    textAlign: 'center',
    color: '#FFFFFF',
    border: 'initial',
    cursor: 'pointer',
    '&:hover': {
      filter: 'brightness(105%)',
    },
  },
  signInBtn: {
    marginBottom: 16
  },
  separateLine: {
    position: 'relative',
    height: '1px',
    width: '100%',
    background: '#E1E1E1',
    margin: '18px 0',
    textAlign: 'center',
    '&::after': {
      content: '"or"',
      position: 'absolute',
      width: '46px',
      height: '20px',
      backgroundColor: '#FFFFFF',
      top: '-10px',
      left: 'calc(50% - 25px)',
      color: '#C4C4C4',
      fontSize: 14,
      fontWeight: 'normal',
    },
  },
  signInFooter: {
    fontSize: '15px',
    lineHeight: '20px',
    textAlign: 'center',

    color: '#757575',
    margin: '16px 0',
  },
  apImg: {
    marginRight: '20px',
    cursor: 'pointer',
  },
  gpImg: {
    cursor: 'pointer',
  },
});

export default withStyles(styles)(EmailSignIn);
